/**
 * Login Page with Firebase Authentication
 * Based on Section 3.0: Authentication Framework
 * Uses react-i18next for internationalization (Section 6.3)
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { auth } from "../lib/firebase";
import { useAuthStore } from "../store/authStore";
import ProMatchTitleLogo from "../components/ProMatchTitleLogo";

export default function Login() {
  const { t } = useTranslation("login");
  const navigate = useNavigate();
  const storeUser = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Single redirect path (PMA-FE-076): navigate only once the app's auth store
  // has the user. App.tsx's onAuthStateChanged handler resolves the role
  // asynchronously and then fills the store, which is what ProtectedRoute
  // trusts. Navigating earlier (right after the popup resolves, or from a
  // Firebase listener here) sends /dashboard to ProtectedRoute while the store
  // is still empty, which bounces back to /login and leaves a window where the
  // URL and the rendered page disagree. `replace` keeps /login out of history.
  useEffect(() => {
    if (storeUser) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate, storeUser]);

  const handleGoogleLogin = async () => {
    setError(null);
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // No navigate here: the storeUser effect redirects once App.tsx has
      // synced the session into the store.
    } catch (err: any) {
      setError(err.message || t("login.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-500 to-primary-700 px-4"
    >
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <ProMatchTitleLogo className="h-32 sm:h-40 w-auto text-primary-600" />
          </div>
          <p
            data-testid="login-subtitle"
            className="text-sm text-gray-500 mt-1"
          >
            {t("subtitle")}
          </p>
        </div>

        {error && (
          <div
            data-testid="login-error"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        <button
          data-testid="login-google-btn"
          onClick={handleGoogleLogin}
          disabled={loading}
          className="btn btn-primary btn-lg w-full"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {loading ? t("signingIn") : t("signInWithGoogle")}
        </button>
      </div>
    </div>
  );
}
